// src/mapBuildings.js
import PopupMap from "./PopupMap.svelte";

const DEFAULT_STANDARD_HEIGHT = 7;
const BUILDINGS_FEATURESET = "buildings";
const STANDARD_INTERACTION_IDS = {
  enter: "template-standard-building-enter",
  leave: "template-standard-building-leave",
  click: "template-standard-building-click",
};

/** All basemap feature IDs for a building (primary + merged part ids). */
function collectBuildingFeatureIds(building) {
  const ids = new Set();
  if (building.featureId) ids.add(String(building.featureId));
  let g = building.geometry;
  if (typeof g === "string") {
    try {
      g = JSON.parse(g);
    } catch (_) {
      g = null;
    }
  }
  if (g?.type === "FeatureCollection" && Array.isArray(g.features)) {
    for (const f of g.features) {
      const alt = f?.properties?.basemapFeatureId;
      if (alt) ids.add(String(alt));
    }
  }
  return Array.from(ids);
}

/** Toggle the global Standard `show3dBuildings` config (true = visible). */
function applyShow3dBuildings(map, importId, show) {
  try {
    map.setConfigProperty(importId, "show3dBuildings", show);
  } catch (_) {}
}

/** Toggle the global Standard `show3dLandmarks` config (landmark 3D buildings; true = visible). */
export function applyShow3dLandmarks(map, importId, show) {
  try {
    map.setConfigProperty(importId, "show3dLandmarks", show);
  } catch (_) {}
}

/** Resolve the basemap import id and toggle the Standard 3D landmark buildings (true = visible). */
export function setBasemapLandmarks(map, show) {
  if (!map) return;
  applyShow3dLandmarks(map, getBasemapImportId(map), show);
}

/**
 * Normalize a stored building geometry into renderable GeoJSON features tagged with the building's
 * `@id` and a per-part `height`. Handles the multi-part FeatureCollection shape (a single building
 * rendered as several 3D parts) as well as legacy single Polygon/MultiPolygon captures.
 */
function buildingRenderFeatures(building, defaultHeight) {
  let g = building.geometry;
  // Defensive: stored geometry may arrive as a JSON string depending on serialization.
  if (typeof g === "string") {
    try {
      g = JSON.parse(g);
    } catch (_) {
      return [];
    }
  }
  if (!g || typeof g !== "object") return [];
  const make = (geometry, height) => ({
    type: "Feature",
    properties: {
      "@id": building.featureId,
      height: height ?? building.height ?? defaultHeight,
    },
    geometry,
  });

  if (g.type === "FeatureCollection" && Array.isArray(g.features)) {
    return g.features
      .filter((f) => f && f.geometry)
      .map((f) => make(f.geometry, f.properties && f.properties.height));
  }
  if (g.type === "Feature" && g.geometry) {
    return [make(g.geometry, g.properties && g.properties.height)];
  }
  if (g.type === "Polygon" || g.type === "MultiPolygon") {
    return [make(g, building.height)];
  }
  return [];
}

/** Best-effort [lng, lat] centroid of a GeoJSON geometry (Polygon/MultiPolygon/Feature/FeatureCollection). */
function centroidOfGeometry(geometry) {
  if (!geometry || typeof geometry !== "object") return null;
  if (geometry.type === "FeatureCollection" && Array.isArray(geometry.features)) {
    const centers = geometry.features
      .map((f) => (f?.geometry ? centroidOfGeometry(f.geometry) : null))
      .filter(Boolean);
    if (!centers.length) return null;
    const sum = centers.reduce((a, c) => [a[0] + c[0], a[1] + c[1]], [0, 0]);
    return [sum[0] / centers.length, sum[1] / centers.length];
  }
  if (geometry.type === "Feature" && geometry.geometry) {
    return centroidOfGeometry(geometry.geometry);
  }
  const coords = [];
  const pushRing = (ring) => {
    if (!Array.isArray(ring)) return;
    for (const c of ring) {
      if (Array.isArray(c) && c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number") {
        coords.push([c[0], c[1]]);
      }
    }
  };
  if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates)) {
    pushRing(geometry.coordinates[0]);
  } else if (geometry.type === "MultiPolygon" && Array.isArray(geometry.coordinates)) {
    for (const poly of geometry.coordinates) pushRing(poly?.[0]);
  }
  if (!coords.length) return null;
  const sum = coords.reduce((a, c) => [a[0] + c[0], a[1] + c[1]], [0, 0]);
  return [sum[0] / coords.length, sum[1] / coords.length];
}

/**
 * Resolve fly-to center for a Mapbox Standard building (source === "mapbox"): stored `center`,
 * then best-effort centroid of stored `geometry`.
 */
export function resolveStandardBuildingCenter(building) {
  if (!building) return null;
  const c = building.center;
  if (Array.isArray(c) && c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number") {
    return [c[0], c[1]];
  }
  let g = building.geometry;
  if (typeof g === "string") {
    try {
      g = JSON.parse(g);
    } catch (_) {
      g = null;
    }
  }
  return centroidOfGeometry(g);
}

/** Centroid of a legacy tileset building footprint by featureId. */
function tilesetBuildingCenter(map, featureId, tilesetLayerName) {
  if (!map || !featureId || !tilesetLayerName) return null;
  try {
    const features = map.querySourceFeatures("base-buildings", {
      sourceLayer: tilesetLayerName,
      filter: ["==", ["get", "@id"], featureId],
    });
    if (!features?.length) return null;
    const geometry = features[0].geometry;
    let allCoordinates = [];
    if (geometry.type === "Polygon") {
      allCoordinates = geometry.coordinates[0];
    } else if (geometry.type === "MultiPolygon") {
      for (const polygon of geometry.coordinates) {
        allCoordinates.push(...polygon[0]);
      }
    }
    if (!allCoordinates.length) return null;
    const center = allCoordinates
      .reduce((acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]], [0, 0])
      .map((s) => s / allCoordinates.length);
    return center;
  } catch (_) {
    return null;
  }
}

/** Point features for Standard building title labels (one label per building). */
export function buildingLabelPointFeaturesStandard(buildings) {
  return (buildings || [])
    .filter((b) => !b.hidden && b.title?.trim())
    .map((b) => {
      const center = resolveStandardBuildingCenter(b);
      if (!center) return null;
      return {
        type: "Feature",
        properties: { title: b.title.trim(), "@id": b.featureId },
        geometry: { type: "Point", coordinates: center },
      };
    })
    .filter(Boolean);
}

/** Point features for legacy tileset building title labels. */
export function buildingLabelPointFeaturesTileset(map, buildings, tilesetLayerName) {
  return (buildings || [])
    .filter((b) => !b.hidden && b.title?.trim() && b.featureId)
    .map((b) => {
      const center = tilesetBuildingCenter(map, b.featureId, tilesetLayerName);
      if (!center) return null;
      return {
        type: "Feature",
        properties: { title: b.title.trim(), "@id": b.featureId },
        geometry: { type: "Point", coordinates: center },
      };
    })
    .filter(Boolean);
}

function addLabelLayerSpec(map, sourceId, layerId) {
  map.addLayer({
    id: layerId,
    type: "symbol",
    slot: "top",
    source: sourceId,
    layout: {
      "text-field": ["get", "title"],
      "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
      "text-size": 16,
      "text-allow-overlap": false,
      "text-optional": true,
    },
    paint: {
      "text-color": "#fff",
      "text-halo-color": "#000",
      "text-halo-width": 2,
    },
    minzoom: 15,
  });
}

/** Add, update, or remove a GeoJSON point symbol label layer. */
export function syncBuildingLabelLayer(map, features, { sourceId, layerId }) {
  if (!map) return;
  const remove = () => {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  };
  if (!features?.length) {
    remove();
    return;
  }
  const data = { type: "FeatureCollection", features };
  if (map.getSource(sourceId)) {
    map.getSource(sourceId).setData(data);
    if (!map.getLayer(layerId)) addLabelLayerSpec(map, sourceId, layerId);
  } else {
    map.addSource(sourceId, { type: "geojson", data });
    addLabelLayerSpec(map, sourceId, layerId);
  }
}

/** Hide title labels for hovered and/or selected buildings so they do not overlap the floating tooltip. */
function syncBuildingLabelHidden(map, layerId, { hoverId = null, selectedId = null } = {}) {
  if (!map?.getLayer(layerId)) return;
  const hideIds = [...new Set([hoverId, selectedId].filter(Boolean))];
  if (!hideIds.length) {
    map.setFilter(layerId);
    return;
  }
  if (hideIds.length === 1) {
    map.setFilter(layerId, ["!=", ["get", "@id"], hideIds[0]]);
    return;
  }
  map.setFilter(layerId, ["!", ["in", ["get", "@id"], ["literal", hideIds]]]);
}

const TILESET_BUILDING_LABEL_LAYER_ID = "tour-buildings-labels-layer";
const STANDARD_BUILDING_LABEL_LAYER_ID = "standard-tour-buildings-labels-layer";

/** Re-apply label filters after layer data changes (e.g. expand/collapse). */
export function refreshBuildingLabelFilters(map) {
  if (!map) return;
  syncBuildingLabelHidden(map, TILESET_BUILDING_LABEL_LAYER_ID, {
    hoverId: map._tilesetBuildingLabelHoverId ?? null,
    selectedId: map._highlightedBuildingId ?? null,
  });
  syncBuildingLabelHidden(map, STANDARD_BUILDING_LABEL_LAYER_ID, {
    hoverId: map._standardBuildingLabelHoverId ?? null,
    selectedId: map._standardMenuHighlightId ?? null,
  });
}

function getBasemapImportId(map) {
  try {
    const style = map.getStyle();
    const imports = style?.imports;
    if (Array.isArray(imports) && imports.length > 0) {
      const basemap = imports.find((i) => i?.id === "basemap");
      return basemap?.id || imports[0]?.id || "basemap";
    }
  } catch (_) {}
  return "basemap";
}

/**
 * Gets the popup configuration for a building
 * @param {Object} building - The building object
 * @param {string} [building.title] - Building title
 * @param {string} [building.popupText] - Popup content text
 * @param {string[]} [building.sceneIds] - Array of scene IDs linked to this building
 * @param {number[]} [building.directions] - Array of direction coordinates
 * @param {Object} [building.popup] - Optional pre-configured popup object
 * @param {Object[]} scenes - Array of available scenes
 * @returns {Object|undefined} Popup configuration object or undefined if no popup
 */
function getPopup(building, scenes) {
  if (!building) return;

  // If building has pre-configured popup object, use it directly
  if (building.popup && typeof building.popup === "object") {
    return building.popup;
  }

  const { title, popupText: content, sceneIds, directions } = building;

  let popup = null;
  const hasPopup =
    (title && content && content.trim().length > 0) ||
    (title && sceneIds?.length > 0) ||
    (title && directions?.length > 0);

  if (hasPopup) {
    // Convert the scene ids into linked scenes with metadata
    const linkedscenes = sceneIds?.map((sceneId) => {
      const scene = scenes?.find((scene) => scene.id === sceneId);
      return {
        id: sceneId,
        name: scene?.name,
        title: scene?.title,
      };
    });

    popup = {
      title,
      content,
      linkedscenes,
      directions,
    };
  }
  return popup;
}

/**
 * Adds interactive 3D buildings to the Mapbox map
 * @param {Object} config - Configuration object
 * @param {Object} config.map - Mapbox map instance
 * @param {string} config.tilesetUrl - URL for the building tileset
 * @param {string} config.tilesetLayerName - Layer name in the tileset
 * @param {string} [config.labelsUrl] - URL for building labels tileset
 * @param {string} [config.labelsLayerName] - Layer name for labels
 * @param {string} config.buildingColor - Default color for buildings
 * @param {string} config.buildingHoverColor - Default hover color for buildings
 * @param {Object[]} config.buildings - Array of building configurations
 * @param {string} config.buildings[].featureId - Unique identifier for the building
 * @param {string} [config.buildings[].color] - Optional custom color for specific building
 * @param {string} [config.buildings[].hoverColor] - Optional custom hover color
 * @param {number} config.buildingHeight - Height for 3D building extrusion
 * @param {Function} config.setTooltip - Callback to update tooltip state
 * @param {Object[]} config.scenes - Array of available scenes
 */
export function addBuildings({
  map,
  tilesetUrl,
  tilesetLayerName,
  labelsUrl,
  labelsLayerName,
  buildingColor,
  buildingHoverColor,
  buildings,
  buildingHeight,
  setTooltip,
  scenes,
  onBuildingClick,
  showOnlyAddedBuildings = false,
  showBuildingLabels = false,
}) {
  // filter out hidden buildings and sort by Map Menu order, then by name
  buildings = buildings.filter((building) => !building.hidden).sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });

  const buildingIds = buildings?.map((i) => i["featureId"]);

  // Create a mapping of custom building colors
  const buildingColors = buildings.reduce((acc, building) => {
    if (building.featureId && building.color) {
      acc[building.featureId] = building.color;
    }
    return acc;
  }, {});

  const layers = [
    {
      id: "tour-buildings-layer",
      color: [
        "case",
        // For each building, check if it has a custom color
        ["has", ["get", "@id"], ["literal", buildingColors]],
        ["get", ["get", "@id"], ["literal", buildingColors]],
        buildingColor // fallback to default color
      ],
      ids: buildingIds,
      showLabels: false,
    },
    process.env.NODE_ENV === "development" && {
      id: "tour-bg-buildings-layer",
      color: "#FF0000",
      ids: buildingIds,
      exclude: true,
    },
  ].filter(Boolean);

  const addLayer = (layer) => {
    let filter;
    if (layer.exclude) {
      filter = ["!", ["in", ["get", "@id"], ["literal", layer.ids]]];
    } else if (showOnlyAddedBuildings) {
      filter = ["in", ["get", "@id"], ["literal", layer.ids]];
    }

    const layerSpec = {
      id: layer.id,
      type: "fill-extrusion",
      source: "base-buildings",
      "source-layer": tilesetLayerName,
      paint: {
        "fill-extrusion-color": layer.color,
        "fill-extrusion-height": buildingHeight,
      },
    };
    if (filter) layerSpec.filter = filter;

    map.addLayer(layerSpec);

    if (layer.showLabels) {
      const buildingLabels = buildings.reduce((acc, cur) => {
        if (cur.featureId && cur.title) {
          acc[cur.featureId] = cur.title;
        }
        return acc;
      }, {});

      const labelSpec = {
        id: layer.id + "-labels",
        type: "symbol",
        source: "labels-buildings",
        "source-layer": labelsLayerName,
        layout: {
          "text-field": ["get", ["get", "@id"], ["literal", buildingLabels]],
          "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
          "text-size": 16,
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: {
          "text-color": "#fff",
          "text-halo-color": "#000",
          "text-halo-width": 2,
        },
        minzoom: 15,
      };
      if (filter) labelSpec.filter = filter;

      map.addLayer(labelSpec);
    }
  };

  map.addSource("base-buildings", {
    type: "vector",
    url: tilesetUrl,
  });

  if (Boolean(labelsUrl && labelsLayerName)) {
    map.addSource("labels-buildings", {
      type: "vector",
      url: labelsUrl,
    });
  }

  layers.forEach(addLayer);

  if (showBuildingLabels) {
    syncBuildingLabelLayer(
      map,
      buildingLabelPointFeaturesTileset(map, buildings, tilesetLayerName),
      { sourceId: "tour-buildings-labels-src", layerId: "tour-buildings-labels-layer" },
    );
  }

  const outlineLayerId = "tour-buildings-outline";
  const tilesetLabelLayerId = TILESET_BUILDING_LABEL_LAYER_ID;

  function refreshTilesetLabelVisibility() {
    syncBuildingLabelHidden(map, tilesetLabelLayerId, {
      hoverId: map._tilesetBuildingLabelHoverId ?? null,
      selectedId: map._highlightedBuildingId ?? null,
    });
  }
  map.addLayer({
    id: outlineLayerId,
    type: "line",
    source: "base-buildings",
    "source-layer": tilesetLayerName,
    filter: ["==", ["get", "@id"], ""],
    paint: {
      "line-color": "#ffffff",
      "line-width": 2.5,
    },
  });

  function handleBuildingSelect(e) {
    if (!e.features) return;
    if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
    const id = e.features[0].properties["@id"];
    const building = buildings.find((b) => b.featureId === id);
    if (onBuildingClick && building) {
      onBuildingClick(building);
    } else {
      const popup = getPopup(building, scenes);
      if (popup) {
        new PopupMap({
          target: document.body,
          props: popup,
        });
      } else {
        console.log(id);
        console.log(e.features[0].properties);
      }
    }
  }

  map.on("click", "tour-buildings-layer", handleBuildingSelect);

  function getPointFromTouch(touchEvent) {
    if (!touchEvent.changedTouches?.length) return null;
    const t = touchEvent.changedTouches[0];
    const canvas = map.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const x = t.clientX - rect.left;
    const y = t.clientY - rect.top;
    if (typeof x !== "number" || typeof y !== "number" || Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
  }

  map.getCanvas().addEventListener("touchend", function buildingTouchEnd(e) {
    try {
      const point = getPointFromTouch(e);
      if (!point || typeof point.x !== "number" || typeof point.y !== "number") return;
      const px = Number(point.x);
      const py = Number(point.y);
      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      const features = map.queryRenderedFeatures({ x: px, y: py }, { layers: ["tour-buildings-layer"] });
      if (!features?.length) return;
      e.preventDefault();
      map._mapboxTouchHandledUntil = Date.now() + 400;
      handleBuildingSelect({ features, point: { x: px, y: py } });
    } catch (_) {}
  }, { passive: false });

  const hoveredLayerId = "tour-buildings-layer";

  function applyBuildingHighlight(featureId) {
    if (!featureId) return;
    const highlightedBuilding = buildings.find((b) => b.featureId === featureId);
    const hoverColorToUse =
      highlightedBuilding?.hoverColor || buildingHoverColor;
    const nonHoveredExpression = [
      "case",
      ["has", ["get", "@id"], ["literal", buildingColors]],
      ["get", ["get", "@id"], ["literal", buildingColors]],
      buildingColor,
    ];
    map.setPaintProperty(hoveredLayerId, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      featureId,
      hoverColorToUse,
      nonHoveredExpression,
    ]);
  }

  /** Reset layer paint to default (no hover, no menu highlight). */
  function resetLayerToDefault() {
    layers.forEach((layer) =>
      map.setPaintProperty(layer.id, "fill-extrusion-color", layer.color),
    );
    map.setPaintProperty(hoveredLayerId, "fill-extrusion-height", buildingHeight);
  }

  function clearBuildingHighlight() {
    map._highlightedBuildingId = null;
    resetLayerToDefault();
    refreshTilesetLabelVisibility();
  }

  map._highlightedBuildingId = null;
  map._tilesetBuildingLabelHoverId = null;
  map.highlightBuilding = function (featureId) {
    map._highlightedBuildingId = featureId || null;
    if (featureId) {
      applyBuildingHighlight(featureId);
    } else {
      clearBuildingHighlight();
    }
    refreshTilesetLabelVisibility();
  };
  map.clearBuildingHighlight = clearBuildingHighlight;

  function onNoFeaturesUnderPointer() {
    if (map._nonBuildingTooltipActive) return;
    setTooltip(null);
    map.getCanvas().style.cursor = "";
    map._tilesetBuildingLabelHoverId = null;
    refreshTilesetLabelVisibility();
    if (map._highlightedBuildingId) {
      const fid = map._highlightedBuildingId;
      const highlightedBuilding = buildings.find((b) => b.featureId === fid);
      const hoverColorToUse =
        highlightedBuilding?.hoverColor || buildingHoverColor;
      const nonHoveredExpression = [
        "case",
        ["has", ["get", "@id"], ["literal", buildingColors]],
        ["get", ["get", "@id"], ["literal", buildingColors]],
        buildingColor,
      ];
      map.setPaintProperty(hoveredLayerId, "fill-extrusion-color", [
        "match",
        ["get", "@id"],
        fid,
        hoverColorToUse,
        nonHoveredExpression,
      ]);
    } else {
      resetLayerToDefault();
    }
  }

  map.getCanvas().addEventListener("mouseleave", onNoFeaturesUnderPointer);

  map.on("mousemove", function (e) {
    const layerIds = layers.map((layer) => layer.id);
    const featuresUnderMouse = map.queryRenderedFeatures(e.point, {
      layers: layerIds,
    });

    if (!featuresUnderMouse.length) {
      onNoFeaturesUnderPointer();
      return;
    }

    const hoveredFeature = featuresUnderMouse[0];
    const hoveredFeatureId = hoveredFeature.properties["@id"];
    if (!hoveredFeatureId) return;
    // Tooltip
    const hoveredBuilding = buildings.find(
      (b) => b.featureId === hoveredFeatureId,
    );
    const tooltipText = hoveredBuilding
      ? hoveredBuilding.title
      : hoveredFeature.properties["name"];
    const tooltipX = e.point.x;
    const tooltipY = e.point.y;
    setTooltip({ text: tooltipText, x: tooltipX, y: tooltipY });
    map._tilesetBuildingLabelHoverId = hoveredFeatureId;
    refreshTilesetLabelVisibility();

    const layer = layers.find((layer) => layer.id === hoveredFeature.layer.id);
    
    // Get custom hover color or use default
    const hoverColorToUse = hoveredBuilding?.hoverColor || buildingHoverColor;

    // For non-hovered buildings, use their custom colors or default
    const nonHoveredExpression = [
      "case",
      ["has", ["get", "@id"], ["literal", buildingColors]],
      ["get", ["get", "@id"], ["literal", buildingColors]],
      layer.color === buildingColor ? buildingColor : layer.color
    ];

    map.setPaintProperty(hoveredFeature.layer.id, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      hoveredFeatureId,
      hoverColorToUse,
      nonHoveredExpression,
    ]);

    // TODO only show pointer if there is a popup
    map.getCanvas().style.cursor = "pointer";
  });
}

/**
 * Adds Mapbox Standard basemap buildings (source === "mapbox") as interactive tour buildings.
 * Renders each added building from its stored GeoJSON footprint as our own `fill-extrusion`
 * layer, clips the originals out of the basemap (so there is no double-render), and lets the
 * `showOnlyAddedBuildings` switch toggle the global `show3dBuildings` config.
 */
export function addStandardBuildings({
  map,
  buildings,
  buildingColor,
  buildingHoverColor,
  setTooltip,
  scenes,
  onBuildingClick,
  showOnlyAddedBuildings = false,
  showLandmarks = true,
  showBuildingLabels = false,
}) {
  if (!map) return;

  const importId = getBasemapImportId(map);

  // The switch only controls whether the basemap's own 3D buildings are visible.
  applyShow3dBuildings(map, importId, !showOnlyAddedBuildings);
  // Independent switch for the basemap's 3D landmark buildings.
  applyShow3dLandmarks(map, importId, showLandmarks);

  const visible = (buildings || []).filter((b) => !b.hidden && b.geometry);
  const byId = new Map(visible.map((b) => [String(b.featureId), b]));
  const featureIdToBuilding = new Map();
  for (const b of visible) {
    for (const id of collectBuildingFeatureIds(b)) {
      featureIdToBuilding.set(id, b);
    }
  }

  if (visible.length === 0) return;

  const SRC_ID = "standard-tour-buildings-src";
  const LAYER_ID = "standard-tour-buildings-layer";
  const CLIP_ID = "standard-buildings-clip";
  const standardLabelLayerId = STANDARD_BUILDING_LABEL_LAYER_ID;

  function refreshStandardLabelVisibility() {
    syncBuildingLabelHidden(map, standardLabelLayerId, {
      hoverId: map._standardBuildingLabelHoverId ?? null,
      selectedId: map._standardMenuHighlightId ?? null,
    });
  }

  const buildingColors = visible.reduce((acc, b) => {
    if (b.featureId && b.color) acc[b.featureId] = b.color;
    return acc;
  }, {});

  const colorExpression = [
    "case",
    ["has", ["get", "@id"], ["literal", buildingColors]],
    ["get", ["get", "@id"], ["literal", buildingColors]],
    buildingColor,
  ];

  const sourceFeatures = visible.flatMap((b) =>
    buildingRenderFeatures(b, DEFAULT_STANDARD_HEIGHT),
  );

  map.addSource(SRC_ID, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: sourceFeatures,
    },
  });

  map.addLayer({
    id: LAYER_ID,
    type: "fill-extrusion",
    // Mapbox Standard slot: place our 3D copies above the basemap labels so they aren't drawn
    // through, and so the layer participates in queryRenderedFeatures (interaction).
    slot: "top",
    source: SRC_ID,
    paint: {
      "fill-extrusion-color": colorExpression,
      "fill-extrusion-height": ["coalesce", ["get", "height"], DEFAULT_STANDARD_HEIGHT],
      "fill-extrusion-opacity": 1,
    },
  });

  // Remove the basemap's own 3D content under our copies. Scoped to the basemap import so our
  // custom fill-extrusion layer is unaffected.
  map.addLayer({
    id: CLIP_ID,
    type: "clip",
    source: SRC_ID,
    layout: {
      "clip-layer-types": ["model", "symbol"],
      "clip-layer-scope": [importId],
    },
  });

  if (showBuildingLabels) {
    syncBuildingLabelLayer(
      map,
      buildingLabelPointFeaturesStandard(visible),
      {
        sourceId: "standard-tour-buildings-labels-src",
        layerId: "standard-tour-buildings-labels-layer",
      },
    );
  }

  function selectBuildingByFeatureId(featureId) {
    const building = byId.get(String(featureId));
    if (!building) return;
    if (onBuildingClick) {
      onBuildingClick(building);
    } else {
      const popup = getPopup(building, scenes);
      if (popup) {
        new PopupMap({ target: document.body, props: popup });
      }
    }
  }

  function applyHover(featureId) {
    const building = byId.get(String(featureId));
    const hoverColorToUse = building?.hoverColor || buildingHoverColor;
    map.setPaintProperty(LAYER_ID, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      featureId,
      hoverColorToUse,
      colorExpression,
    ]);
  }

  function resetColor() {
    map.setPaintProperty(LAYER_ID, "fill-extrusion-color", colorExpression);
  }

  // Basemap interactions use 3D picking (works for the primary footprint at pitch). Merged parts
  // are clipped from the basemap and only exist on our fill-extrusion layer — supplement with
  // queryRenderedFeatures on that layer (tileset-building pattern; layer-bound events do not fire).
  for (const id of Object.values(STANDARD_INTERACTION_IDS)) {
    try {
      map.removeInteraction(id);
    } catch (_) {}
  }

  let hoveredBasemapFeatureId = null;
  let hoveredLayerFeatureId = null;
  map._standardBuildingLabelHoverId = null;
  const interactionTarget = { featuresetId: BUILDINGS_FEATURESET, importId };

  function showBuildingTooltip(building, point) {
    if (!building?.title || !setTooltip) return;
    const x = point?.x ?? 0;
    const y = point?.y ?? 0;
    setTooltip({ text: building.title, x, y });
  }

  function setHoverForBuilding(building, point) {
    const fid = String(building.featureId);
    applyHover(fid);
    showBuildingTooltip(building, point);
    map._standardBuildingLabelHoverId = fid;
    refreshStandardLabelVisibility();
    map.getCanvas().style.cursor = "pointer";
  }

  function clearHoverState() {
    hoveredBasemapFeatureId = null;
    hoveredLayerFeatureId = null;
    if (map._standardMenuHighlightId) {
      applyHover(map._standardMenuHighlightId);
    } else {
      resetColor();
    }
    if (setTooltip && !map._nonBuildingTooltipActive) setTooltip(null);
    map._standardBuildingLabelHoverId = null;
    refreshStandardLabelVisibility();
    map.getCanvas().style.cursor = "";
  }

  function onNoStandardFeaturesUnderPointer() {
    if (hoveredBasemapFeatureId || hoveredLayerFeatureId) return;
    clearHoverState();
  }

  // mouseenter only fires at boundaries; follow the cursor while a basemap feature is hovered.
  const onMapMouseMoveForBasemapTooltip = (e) => {
    if (!hoveredBasemapFeatureId) return;
    const building = featureIdToBuilding.get(hoveredBasemapFeatureId);
    if (building) showBuildingTooltip(building, e.point);
  };
  if (map._standardBuildingTooltipMove) {
    map.off("mousemove", map._standardBuildingTooltipMove);
  }
  map._standardBuildingTooltipMove = onMapMouseMoveForBasemapTooltip;
  map.on("mousemove", onMapMouseMoveForBasemapTooltip);

  try {
    map.addInteraction(STANDARD_INTERACTION_IDS.enter, {
      type: "mouseenter",
      target: interactionTarget,
      handler: (e) => {
        const feature = e?.feature;
        if (!feature) return;
        const building = featureIdToBuilding.get(String(feature.id));
        if (!building) return;
        hoveredBasemapFeatureId = String(feature.id);
        setHoverForBuilding(building, e?.point);
      },
    });

    map.addInteraction(STANDARD_INTERACTION_IDS.leave, {
      type: "mouseleave",
      target: interactionTarget,
      handler: (e) => {
        const feature = e?.feature;
        if (hoveredBasemapFeatureId && feature && String(feature.id) === hoveredBasemapFeatureId) {
          hoveredBasemapFeatureId = null;
          if (!hoveredLayerFeatureId) clearHoverState();
        }
      },
    });

    map.addInteraction(STANDARD_INTERACTION_IDS.click, {
      type: "click",
      target: interactionTarget,
      handler: (e) => {
        if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
        const feature = e?.feature;
        if (!feature) return;
        const building = featureIdToBuilding.get(String(feature.id));
        if (!building) return;
        selectBuildingByFeatureId(String(building.featureId));
      },
    });
  } catch (err) {
    console.warn("[template standard-buildings] addInteraction unavailable", err);
  }

  map.on("mousemove", function standardBuildingLayerMouseMove(e) {
    const featuresUnderMouse = map.queryRenderedFeatures(e.point, { layers: [LAYER_ID] });
    if (!featuresUnderMouse.length) {
      hoveredLayerFeatureId = null;
      onNoStandardFeaturesUnderPointer();
      return;
    }

    const fid = featuresUnderMouse[0].properties?.["@id"];
    const building = fid ? byId.get(String(fid)) : null;
    if (!building) return;

    hoveredLayerFeatureId = String(fid);
    setHoverForBuilding(building, e.point);
  });

  map.getCanvas().addEventListener("mouseleave", onNoStandardFeaturesUnderPointer);

  map.on("click", function standardBuildingLayerClick(e) {
    if (map._mapboxTouchHandledUntil && Date.now() < map._mapboxTouchHandledUntil) return;
    const features = map.queryRenderedFeatures(e.point, { layers: [LAYER_ID] });
    const fid = features?.[0]?.properties?.["@id"];
    if (!fid || !byId.has(String(fid))) return;
    selectBuildingByFeatureId(String(fid));
  });

  map.getCanvas().addEventListener("touchend", function standardBuildingTouchEnd(e) {
    try {
      if (!e.changedTouches?.length) return;
      const t = e.changedTouches[0];
      const canvas = map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const x = t.clientX - rect.left;
      const y = t.clientY - rect.top;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const features = map.queryRenderedFeatures({ x, y }, { layers: [LAYER_ID] });
      const fid = features?.[0]?.properties?.["@id"];
      if (!fid || !byId.has(String(fid))) return;
      e.preventDefault();
      map._mapboxTouchHandledUntil = Date.now() + 400;
      selectBuildingByFeatureId(String(fid));
    } catch (_) {}
  }, { passive: false });

  // Integrate with the map menu highlight API (chain with any tileset implementation).
  const prevHighlight = map.highlightBuilding;
  const prevClear = map.clearBuildingHighlight;
  map._standardMenuHighlightId = null;

  map.highlightBuilding = function (featureId) {
    if (byId.has(String(featureId))) {
      map._standardMenuHighlightId = featureId;
      applyHover(featureId);
      refreshStandardLabelVisibility();
    } else if (prevHighlight) {
      prevHighlight(featureId);
    }
  };

  map.clearBuildingHighlight = function () {
    if (map._standardMenuHighlightId) {
      map._standardMenuHighlightId = null;
      resetColor();
      refreshStandardLabelVisibility();
    }
    if (prevClear) prevClear();
  };
}
